import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { Field } from 'react-final-form'
import ReactTooltip from 'react-tooltip'
import Switch from '../../components/Switch'
import AgreementContentEditor from '../../components/agreement-content-editor'
import { TextInput } from '../../components/TextInput'
import { SelectInput } from '../../components/SelectInput'
import {
  SIGNATURE_TRIGGER_OPTIONS,
  DELIVERY_OPTIONS,
  LINK_EXPIRATION_OPTIONS,
  NOTIFY_OPTIONS,
  DEFAULT_TEMPLATE_TIP,
  WARN_TICKET_TIP,
  WARN_INVOICE_TIP
} from './constants'
import { normalizeCustomDays } from './utils'
import styles from './index.module.scss'

function NameAndDefaultRow () {
  return (
    <div className={styles.nameRow}>
      <div className={styles.nameField}>
        <Field
          name='name'
          component={TextInput}
          label='Template Name'
          placeholder='Template Name'
          maxLength='50'
        />
      </div>
      <div className={styles.defaultCheckbox}>
        <span className={styles.toggleLabel}>
          <label htmlFor='isDefault'>Set as Default</label>
          <i
            className={cn('material-icons', styles.infoIcon)}
            data-tip={DEFAULT_TEMPLATE_TIP}
            data-for='agreement-default-tooltip'>
            info
          </i>
        </span>
        <Field name='isDefault' type='checkbox'>
          {({ input }) => (
            <input
              id='isDefault'
              type='checkbox'
              checked={!!input.checked}
              onChange={input.onChange}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              name={input.name}
              className={cn(styles.checkboxInput)}
            />
          )}
        </Field>
      </div>
    </div>
  )
}

function ContentEditorField ({ isDisabled, editorId, onPreview }) {
  return (
    <div className={styles.editorSection}>
      <label htmlFor={editorId} className={cn('control-label', styles.nameField)}>
        Terms and Conditions
      </label>
      <Field name='content'>
        {({ input, meta }) => (
          <div className={styles.editorWrapper}>
            <AgreementContentEditor
              id={editorId}
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              disabled={isDisabled}
            />
            {(meta.touched || meta.submitFailed) && meta.error && (
              <p id={`${editorId}-error`} className={styles.editorError} role='alert'>{meta.error}</p>
            )}
            <button
              type='button'
              className={cn('dis-btn dis-btn-gray', styles.previewBtn)}
              onClick={() => onPreview(input.value)}>
              PREVIEW
            </button>
          </div>
        )}
      </Field>
    </div>
  )
}

function WarningToggles ({ values, form }) {
  return (
    <div className={styles.toggleRow}>
      <div className={styles.toggleItem}>
        <span className={styles.toggleLabel}>
          Display Warning on Ticket Creation
          <i
            className={cn('material-icons', styles.infoIcon)}
            data-tip={WARN_TICKET_TIP}
            data-for='agreement-warn-tooltip'>
            info
          </i>
        </span>
        <Switch
          size='small'
          checked={values.warnTicketCreation}
          onChange={(checked) => form.change('warnTicketCreation', checked)}
        />
      </div>
      <div className={styles.toggleDivider} />
      <div className={styles.toggleItem}>
        <span className={styles.toggleLabel}>
          Display Warning on Invoice Creation
          <i
            className={cn('material-icons', styles.infoIcon)}
            data-tip={WARN_INVOICE_TIP}
            data-for='agreement-warn-tooltip'>
            info
          </i>
        </span>
        <Switch
          size='small'
          checked={values.warnInvoicePayment}
          onChange={(checked) => form.change('warnInvoicePayment', checked)}
        />
      </div>
    </div>
  )
}

function DeliverySettings ({ values }) {
  return (
    <>
      <div className={styles.dropdownGrid}>
        <Field
          name='signatureTrigger'
          component={SelectInput}
          label='Send Terms and Conditions'
          options={SIGNATURE_TRIGGER_OPTIONS}
        />
        <Field
          name='defaultDelivery'
          component={SelectInput}
          label='Default Delivery'
          options={DELIVERY_OPTIONS}
        />
      </div>
      <div className={values.linkExpiration === 'CUSTOM' ? styles.dropdownRowCustom : styles.dropdownRow}>
        <Field
          name='linkExpiration'
          component={SelectInput}
          label='Link Expiration'
          options={LINK_EXPIRATION_OPTIONS}
        />
        {values.linkExpiration === 'CUSTOM' && (
          <Field
            name='customDays'
            component={TextInput}
            label='Enter Custom Days'
            parse={normalizeCustomDays}
          />
        )}
        <Field
          name='notifyAdminOnSign'
          component={SelectInput}
          label='Email Admins When Terms and Conditions Are Signed'
          options={NOTIFY_OPTIONS}
        />
      </div>
    </>
  )
}

export default function AgreementFormFields ({ form, values, isDisabled, editorId, onPreview }) {
  return (
    <div className={cn('dis-panel', styles.formPanel)}>
      <div className='dis-panel-body'>
        <NameAndDefaultRow />
        <ContentEditorField
          isDisabled={isDisabled}
          editorId={editorId}
          onPreview={onPreview}
        />
        <WarningToggles values={values} form={form} />
        <DeliverySettings values={values} />
        <ReactTooltip
          id='agreement-default-tooltip'
          place='bottom'
          effect='solid'
          multiline
          className={styles.tooltip}
        />
        <ReactTooltip
          id='agreement-warn-tooltip'
          place='top'
          effect='solid'
          multiline
          className={styles.tooltip}
        />
      </div>
    </div>
  )
}

ContentEditorField.propTypes = {
  isDisabled: PropTypes.bool,
  editorId: PropTypes.string.isRequired,
  onPreview: PropTypes.func.isRequired
}

WarningToggles.propTypes = {
  values: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired
}

DeliverySettings.propTypes = {
  values: PropTypes.object.isRequired
}

AgreementFormFields.propTypes = {
  form: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
  editorId: PropTypes.string.isRequired,
  onPreview: PropTypes.func.isRequired
}
